import { useWeb3DebugContext } from "@/components/Web3/Debug/State";
import type { SupSingleVaultV3, SupWithdrawableToken, Networkish } from "@/types";
import React, { type SetStateAction, type Dispatch, useMemo, useState, useCallback } from "react";

import { type Column, SupDataGrid, SupText, SupTooltip } from "@repo/core-ui";
import { SupSearch } from "@repo/core-ui";
import { FixedNumber } from "@ethersproject/bignumber";
import UiChainSelector from "@/components/Ui/Widget/ChainSelector/Wrapper";
import GridCellPinToken from "@/components/Grid/Cell/PinToken";
import UiPinnedToken from "@/components/Ui/PinnedToken";
import UiTag from "@/components/Ui/Tag";
import UiTooltip from "@/components/Ui/Tooltip";

import { useStoredData, useStoredDataDispatch } from "@/store";
import dappConfig from "@/../config/dapp.config";
import useSupportedWithdrawableTokens from "@/hooks/api/useSupportedWithdrawableTokens";
import { twMerge } from "tailwind-merge";
import { arrayIntersect, formatCurrencyCompact, getWeb3Address, notifyCommon } from "@/utils";
import { isAddressEqual, isAddress, type Address } from "viem";

interface Web3WithdrawTargetTokenProps {
  setSelectedBalance: Dispatch<SetStateAction<SupWithdrawableToken | undefined>>;
  vaultList: SupSingleVaultV3[];
}

export default function Web3WithdrawTargetToken({ setSelectedBalance, vaultList }: Web3WithdrawTargetTokenProps) {
  const { data: supportedTokens, isLoading: isBalanceLoading } = useSupportedWithdrawableTokens(vaultList);
  const { reset } = useWeb3DebugContext();

  const [query, setQuery] = useState<string>("");

  const storedData = useStoredData();
  const storedDispatch = useStoredDataDispatch();

  const balanceChains = useMemo(() => {
    const chainList = supportedTokens?.filter(b => !!b.chainId).map(b => b.chainId);
    return (chainList ? [...new Set(chainList)] : []) as Networkish[];
  }, [supportedTokens]);

  const selectedChains = useMemo(() => {
    return arrayIntersect(storedData.tokenSelectorChains, balanceChains);
  }, [balanceChains, storedData.tokenSelectorChains]);

  const filterBalance = useMemo(() => {
    let results = supportedTokens ? supportedTokens.slice() : ([] as SupWithdrawableToken[]);
    if (results.length === 0) {
      return results;
    }
    if (query.length > 1) {
      results = results.filter(singleToken => {
        return isAddress(query) ? isAddressEqual(query as Address, singleToken.address) : singleToken.symbol.toLowerCase().includes(query.toLowerCase());
      });
    }

    if (selectedChains.length > 0 && selectedChains.length !== balanceChains.length) {
      results = results.filter(singleBalance => selectedChains.includes(singleBalance.chainId));
    }
    return results;
  }, [balanceChains.length, query, selectedChains, supportedTokens]);

  const handleTokenSelection = useCallback(
    (balance: SupWithdrawableToken) => {
      if (!dappConfig.tx.avaxCrossChain && vaultList.find(vault => vault.chain.id === 43114) && balance.chainId !== 43114) {
        notifyCommon("Choose another token: No cross-chain withdrawals allowed for AVAX at the moment", false, "error", 3000);
        return;
      }
      if (balance.isUnsupported) {
        notifyCommon("Choose another token: this one is unsupported within Superform App", false, "error", 3000);
        return;
      }
      setSelectedBalance(balance);
      reset?.();
    },
    [reset, setSelectedBalance, vaultList]
  );

  const columns: Column<SupWithdrawableToken>[] = [
    {
      cellClassName: "cursor-pointer",
      field: "symbol",
      width: "1fr",
      resizable: false,
      draggable: false,
      headerName: "Token",
      valueGetter: row => `${row.symbol} ${row.address}`,
      renderCell: row => <UiTag asset={row} chainId={row.chainId} />,
    },
    {
      cellClassName: "cursor-pointer",
      resizable: false,
      width: 100,
      maxWidth: 100,
      draggable: false,
      field: "amountFixedNumber",
      type: "number",
      align: "right",
      headerName: "Balance",
      valueGetter: row => (row.amountFixedNumber ? (row.amountFixedNumber as FixedNumber).round(dappConfig.decimalsBigRoundingLimit).toUnsafeFloat() : 0),
      renderCell: row => {
        if (!row.amountFixedNumber) return;

        const amount = (row.amountFixedNumber as FixedNumber).round(dappConfig.decimalsBigRoundingLimit).toString();
        const isCompact = amount.length > 13;

        return (
          <SupTooltip placement="top" unmount portal content={row.amountFixedNumber ? (row.amountFixedNumber as FixedNumber).toString() : ""} color="primary">
            <span className={twMerge(row.isUnsupported && "opacity-44", "cursor-help text-[13px]")}>
              {isCompact ? formatCurrencyCompact(parseFloat(amount), 2, 0, "decimal") : amount}
            </span>
          </SupTooltip>
        );
      },
    },
    {
      cellClassName: "cursor-pointer",
      resizable: false,
      width: 80,
      maxWidth: 80,
      field: "amountInFiat",
      type: "number",
      align: "right",
      headerName: "USD",
      valueGetter: row => (row.amountInFiat ? parseFloat(row.amountInFiat as string) : -1),
      renderCell: row =>
        row.amountInFiat ? (
          <span className={twMerge(row.isUnsupported && "opacity-44", "text-[16px]")}>{formatCurrencyCompact(parseFloat(row.amountInFiat as string))}</span>
        ) : (
          <></>
        ),
    },
    {
      resizable: false,
      draggable: false,
      width: 30,
      maxWidth: 30,
      field: "pin",
      sortable: false,
      align: "right",
      headerName: "",
      renderCell: row => <GridCellPinToken singleBalance={row} />,
    },
  ];

  const mappedTokenBalances = useMemo(() => {
    if (!supportedTokens) {
      return [];
    }
    return supportedTokens.filter(
      singleToken =>
        storedData.favTokens.find(favToken => getWeb3Address(favToken.tokenAddress, favToken.chainId) === getWeb3Address(singleToken.address, singleToken.chainId)) !== undefined ||
        singleToken.isVaultToken
    ) as SupWithdrawableToken[];
  }, [supportedTokens, storedData.favTokens]);

  const handleChainSelection = (singleChain: Networkish) => {
    const preparedSelectedChains = selectedChains.includes(singleChain) ? selectedChains.filter(chain => chain !== singleChain) : [...selectedChains, singleChain];
    storedDispatch({ type: "setTokenSelectorChains", tokenSelectorChains: preparedSelectedChains });
  };
  return (
    <div className="flex h-full flex-col space-y-6 overflow-hidden" id="balance-container">
      <div className="relative">
        <SupSearch className="absolute left-2.5 top-2.5 z-10 h-5 w-5 stroke-0 text-neutral-900" />
        <input
          type="text"
          autoComplete="off"
          autoCapitalize="all"
          id="balance-search"
          className="h-10 w-full justify-center border-0 bg-neutral-50 p-2 pl-8 normal-case placeholder-gray-400 outline-none"
          value={query}
          placeholder="SEARCH SYMBOL/ADDRESS"
          onChange={e => setQuery(e.target.value)}
        />
      </div>

      <UiChainSelector defaultExpanded chainList={balanceChains} selectedChains={selectedChains} onClick={handleChainSelection} />

      {mappedTokenBalances.length > 0 && (
        <>
          <SupText variant="subtitle1" as="h3">
            Pinned tokens
          </SupText>
          <ul className="flex flex-wrap items-center gap-[10px]">
            {mappedTokenBalances.map(pinnedBalance => (
              <UiTooltip
                enable={pinnedBalance.isVaultToken === true}
                arrowPlacement="left"
                content="A vault token"
                key={`balance-token-${pinnedBalance.address}-${pinnedBalance.chainId}`}
              >
                <span>
                  <UiPinnedToken clickHandler={handleTokenSelection} token={pinnedBalance} />
                </span>
              </UiTooltip>
            ))}
          </ul>
        </>
      )}

      <div className="flex-grow overflow-hidden">
        <SupDataGrid
          enableVirtualization
          loading={isBalanceLoading}
          className="custom-scrollbar h-full overflow-x-hidden p-0"
          defaultSorting={[{ field: "amountInFiat", direction: "DESC" }]}
          onRowClick={row => {
            handleTokenSelection(row as SupWithdrawableToken);
          }}
          data={filterBalance}
          rowId={row => `${row.address}_${row.chainId}`}
          columns={columns}
        />
      </div>
    </div>
  );
}
